import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { ArrowLeft, AlertTriangle, User, MapPin, Phone, Mail, CheckCircle2, XCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { learnEarnAPI } from '../../services/api';
import { formValidation } from '../../utils/formValidation';

export function WorkshopForm({ workItem, onBack }) {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    whatsapp: '',
    email: '',
    sameAsPhone: false,
    completeOnTime: 'yes',
    rulesAccepted: false,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'sameAsPhone' && value === true ? { whatsapp: prev.phone } : {}),
    }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const validationErrors = formValidation.validateWorkshopForm(formData);
    const newErrors = {};
    
    validationErrors.forEach(error => {
      if (error.includes('Name')) newErrors.name = error;
      else if (error.includes('Address')) newErrors.address = error;
      else if (error.includes('Phone')) newErrors.phone = error;
      else if (error.includes('Email')) newErrors.email = error;
      else if (error.includes('WhatsApp')) newErrors.whatsapp = error;
      else if (error.includes('time')) newErrors.completeOnTime = error;
      else if (error.includes('rules')) newErrors.rulesAccepted = error;
    });

    setErrors(newErrors);
    return validationErrors.length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm() && !isSubmitting) {
      setIsSubmitting(true);

      try {
        console.log('🔍 Workshop Form Debug:', formData);

        const resolvedId = workItem._id || workItem.id;

        // Guard: ensure it's a valid MongoDB ObjectId (24-char hex)
        if (!resolvedId || !/^[a-f\d]{24}$/i.test(String(resolvedId))) {
          alert('❌ This work item cannot be applied for — it has an invalid ID. Please go back and select a valid work item from the list.');
          setIsSubmitting(false);
          return;
        }

        // Format data using utility
        const applicationData = formValidation.formatWorkshopData({
          ...formData,
          workItemId: resolvedId,
        });

        console.log('📤 Final workshop data being sent:', applicationData);

        const response = await learnEarnAPI.workshop.submitApplication(applicationData);

        console.log('✅ Workshop application submitted successfully');
        alert(`Success! ${response.message || 'Application submitted successfully!'}`);
        onBack();

      } catch (error) {
        console.error('❌ Workshop application submission error:', error);
        alert(`Error: ${error.message || 'Failed to submit application. Please try again.'}`);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div>
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
      >
        <Button 
          variant="ghost" 
          onClick={onBack}
          className="mb-6 hover:bg-accent"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Work List
        </Button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="border border-border shadow-2xl bg-card backdrop-blur-sm overflow-hidden">
          <div className={`h-2 ${workItem.type === 'assignment' ? 'bg-blue-600' : 'bg-emerald-500'}`}></div>
          <CardHeader className={`${workItem.type === 'assignment' ? 'bg-blue-600' : 'bg-emerald-500'} text-white`}>
            <CardTitle className="text-2xl">{workItem.title}</CardTitle>
            <CardDescription className="text-primary-foreground/90">
              {workItem.description}
            </CardDescription>
            <div className="flex gap-6 mt-4 text-sm flex-wrap">
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg">
                <span className="opacity-90">Pages:</span> 
                <span className="font-bold">{workItem.pages}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg">
                <span className="opacity-90">Payment:</span> 
                <span className="font-bold">₹{workItem.amount}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg">
                <span className="opacity-90">Deadline:</span> 
                <span className="font-bold">{new Date(workItem.deadline).toLocaleDateString()}</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-8">
            {/* Privacy Notice */}
            <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div>
                  <p className="text-emerald-700 dark:text-emerald-400 font-semibold text-sm">🔒 Privacy Protected</p>
                  <p className="text-emerald-600 dark:text-emerald-500 text-xs">Your personal data is securely protected and will not be shared with anyone. All interactions are anonymous.</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Personal Details */}
              <div className="space-y-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${workItem.type === 'assignment' ? 'bg-blue-600' : 'bg-emerald-500'}`}>
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <h3 className={`text-xl font-semibold ${workItem.type === 'assignment' ? 'text-blue-600' : 'text-emerald-600'}`}>
                    Personal Details
                  </h3>
                </div>
                
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-muted-foreground flex items-center gap-2">
                      <User className={`w-4 h-4 ${workItem.type === 'assignment' ? 'text-accent' : 'text-emerald-500'}`} />
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className={`mt-2 border ${errors.name ? 'border-destructive' : workItem.type === 'assignment' ? 'border-border focus:border-primary' : 'border-emerald-500/30 focus:border-emerald-500'} bg-background/50`}
                      placeholder="Enter your full name"
                    />
                    {errors.name && <p className="text-destructive text-sm mt-1 flex items-center gap-1">⚠ {errors.name}</p>}
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-muted-foreground flex items-center gap-2">
                      <Mail className={`w-4 h-4 ${workItem.type === 'assignment' ? 'text-accent' : 'text-emerald-500'}`} />
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className={`mt-2 border ${errors.email ? 'border-destructive' : workItem.type === 'assignment' ? 'border-border focus:border-primary' : 'border-emerald-500/30 focus:border-emerald-500'} bg-background/50`}
                      placeholder="your.email@example.com"
                    />
                    {errors.email && <p className="text-destructive text-sm mt-1 flex items-center gap-1">⚠ {errors.email}</p>}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="phone" className="text-muted-foreground flex items-center gap-2">
                      <Phone className={`w-4 h-4 ${workItem.type === 'assignment' ? 'text-accent' : 'text-emerald-500'}`} />
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                        handleChange('phone', value);
                        if (formData.sameAsPhone) {
                          handleChange('whatsapp', value);
                        }
                      }}
                      className={`mt-2 border-2 ${errors.phone ? 'border-red-400' : workItem.type === 'assignment' ? 'border-cyan-200 focus:border-cyan-500' : 'border-green-200 focus:border-green-500'} bg-white/70`}
                      placeholder="+91 1234567890"
                      maxLength={10}
                    />
                    {errors.phone && <p className="text-destructive text-sm mt-1 flex items-center gap-1">⚠ {errors.phone}</p>}
                  </div>

                  <div>
                    <Label htmlFor="whatsapp" className="text-muted-foreground flex items-center gap-2">
                      <Phone className="w-4 h-4 text-emerald-500" />
                      WhatsApp Number
                    </Label>
                    <Input
                      id="whatsapp"
                      type="tel"
                      value={formData.whatsapp}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                        handleChange('whatsapp', value);
                      }}
                      disabled={formData.sameAsPhone}
                      className="mt-2 border-2 border-green-200 focus:border-green-500 bg-white/70 disabled:bg-gray-100"
                      placeholder="+91 1234567890"
                      maxLength={10}
                    />
                    {errors.whatsapp && <p className="text-destructive text-sm mt-1 flex items-center gap-1">⚠ {errors.whatsapp}</p>}
                    <div className="flex items-center mt-2">
                      <Checkbox
                        id="sameAsPhone"
                        checked={formData.sameAsPhone}
                        onCheckedChange={(checked) => handleChange('sameAsPhone', checked)}
                        className="border-emerald-500"
                      />
                      <Label htmlFor="sameAsPhone" className="ml-2 cursor-pointer text-sm text-muted-foreground">
                        Same as phone number
                      </Label>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="address" className="text-muted-foreground flex items-center gap-2">
                    <MapPin className={`w-4 h-4 ${workItem.type === 'assignment' ? 'text-accent' : 'text-emerald-500'}`} />
                    Address *
                  </Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    className={`mt-2 border ${errors.address ? 'border-destructive' : workItem.type === 'assignment' ? 'border-border focus:border-primary' : 'border-emerald-500/30 focus:border-emerald-500'} bg-background/50`}
                    placeholder="Enter your complete address"
                  />
                  {errors.address && <p className="text-destructive text-sm mt-1 flex items-center gap-1">⚠ {errors.address}</p>}
                </div>
              </div>

              {/* Commitment */}
              <div className="space-y-4">
                <h3 className={`text-lg font-semibold ${workItem.type === 'assignment' ? 'text-blue-600' : 'text-emerald-700'}`}>
                  Commitment
                </h3>
                
                <div>
                  <Label className="text-muted-foreground font-semibold mb-3 block">
                    Will you do the work on or before time? *
                  </Label>
                  <RadioGroup
                    value={formData.completeOnTime}
                    onValueChange={(value) => handleChange('completeOnTime', value)}
                    className="grid md:grid-cols-2 gap-4"
                  >
                    <div className={`relative flex items-center space-x-3 p-4 border rounded-xl cursor-pointer transition-all ${formData.completeOnTime === 'yes' ? 'border-emerald-500 bg-emerald-500/10' : 'border-border bg-card hover:border-emerald-500/50'}`}>
                      <RadioGroupItem value="yes" id="yes" className="border-emerald-500" />
                      <Label htmlFor="yes" className="cursor-pointer flex-1">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                          <span className="font-semibold text-foreground">Yes, I will!</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">I commit to completing on time</p>
                      </Label>
                    </div>
                    <div className={`relative flex items-center space-x-3 p-4 border rounded-xl cursor-pointer transition-all ${formData.completeOnTime === 'no' ? 'border-destructive bg-destructive/10' : 'border-border bg-card hover:border-destructive/50'}`}>
                      <RadioGroupItem value="no" id="no" className="border-destructive" />
                      <Label htmlFor="no" className="cursor-pointer flex-1">
                        <div className="flex items-center gap-2">
                          <XCircle className="w-5 h-5 text-destructive" />
                          <span className="font-semibold text-foreground">No</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">I might need more time</p>
                      </Label>
                    </div>
                  </RadioGroup>
                  {errors.completeOnTime && <p className="text-destructive text-sm mt-2 flex items-center gap-1">⚠ {errors.completeOnTime}</p>}
                  {formData.completeOnTime === 'no' && (
                    <div className="mt-3 p-4 bg-destructive/10 border border-destructive/30 rounded-xl">
                      <p className="text-sm text-destructive font-semibold flex items-center gap-2">
                        <XCircle className="w-4 h-4" />
                        You cannot apply for this work if you cannot commit to completing it on time.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Rules and Regulations */}
              <div className="space-y-4 bg-yellow-500/10 p-6 rounded-2xl border border-yellow-500/20">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-yellow-700 dark:text-yellow-500 mb-1">Rules and Regulations</h3>
                    <p className="text-sm text-yellow-600 dark:text-yellow-400">Please read carefully before proceeding</p>
                  </div>
                </div>
                
                <div className="space-y-4 text-sm">
                  <div className="flex gap-3 p-4 bg-background/50 rounded-xl">
                    <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                      1
                    </div>
                    <p className="text-muted-foreground flex-1">
                      After accepting the work, you <span className="font-bold text-yellow-700 dark:text-yellow-500">MUST</span> complete it on or before the deadline mentioned above ({new Date(workItem.deadline).toLocaleDateString()}).
                    </p>
                  </div>

                  <div className="flex gap-3 p-4 bg-background/50 rounded-xl">
                    <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                      2
                    </div>
                    <p className="text-muted-foreground flex-1">
                      You <span className="font-bold text-yellow-700 dark:text-yellow-500">MUST</span> provide updates on your work progress 2-3 days before the deadline. If your work has not progressed satisfactorily, we reserve the right to reassign the work to another person, and you will <span className="font-bold text-destructive">NOT</span> be paid.
                    </p>
                  </div>

                  <div className="flex gap-3 p-4 bg-background/50 rounded-xl">
                    <div className="w-8 h-8 bg-destructive rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                      3
                    </div>
                    <p className="text-muted-foreground flex-1">
                      If journals/assignments are sent to you and they are misplaced, damaged, or lost, you will be required to <span className="font-bold text-destructive">pay for the replacement</span>. Strict actions will be taken against you, and you will be <span className="font-bold text-destructive">BLACKLISTED</span> from A.G.O. You will not be able to use our website again.
                    </p>
                  </div>

                  <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-xl">
                    <p className="text-sm text-destructive font-semibold">
                      ⚠️ Warning: Failure to comply with these rules will result in permanent ban from the platform and potential legal action.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 pt-4 border-t border-yellow-500/20">
                  <Checkbox
                    id="rulesAccepted"
                    checked={formData.rulesAccepted}
                    onCheckedChange={(checked) => handleChange('rulesAccepted', checked)}
                    className="mt-1 border-yellow-500"
                  />
                  <Label 
                    htmlFor="rulesAccepted" 
                    className="cursor-pointer text-sm leading-relaxed text-muted-foreground"
                  >
                    I have read and understood all the rules and regulations mentioned above. I agree to comply with all terms and conditions.
                  </Label>
                </div>
                {errors.rulesAccepted && (
                  <p className="text-destructive text-sm font-semibold flex items-center gap-1">⚠ {errors.rulesAccepted}</p>
                )}
              </div>

              <Button 
                type="submit" 
                className={`w-full py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all ${
                  workItem.type === 'assignment'
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-emerald-600 hover:bg-emerald-700'
                } text-white border-0 disabled:opacity-50 disabled:cursor-not-allowed`}
                disabled={!formData.rulesAccepted || formData.completeOnTime === 'no' || isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></div>
                    Submitting Application...
                  </span>
                ) : formData.completeOnTime === 'no' ? (
                  'Cannot Submit - Must Commit to Complete on Time'
                ) : (
                  'Submit Application ✨'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
